import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Text } from '@uhg-abyss/web/ui/Text';
import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import update from 'lodash/update';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { adobeLinkTrackEvent } from '../../../../common/AdobeTagging/adobeLinkTrackEvent';
import { useLocation } from '../../../../hooks/useLocation';
import { refactorPlaceName } from '../../../../utils/locationSearch.util';
import {
  FindUserLocationContainer,
  StyledLocationError,
  StyledLocationHeader,
  StyledLocationItems,
  StyledLocationSearch,
} from './LocationInput.styled';

type Props = {
  findUserLocation?: Function;
  setCustomLocation?: (a: object) => void;
};

export const LocationInput = ({
  findUserLocation = () => {},
  setCustomLocation = () => {},
}: Props) => {
  const { t } = useTranslation();
  const locationSearchInputDataTestId: string = 'location-search-input';
  const locationForAnalytics: string = 'change search location';

  const [isLoading, setIsLoading] = useState(false);
  const [gqlSuggestions, setgqlSuggestions] = useState<object[]>([]);
  const [errorMessage, setErrorMessage] = useState('');

  const [, getLocation] = useLocation({
    onCompleted: (result: {
      data: {
        location: {
          features: [{ place_name: string; id: string }];
        };
      };
    }) => {
      const resultSuggestions = cloneDeep(result?.data?.location?.features);
      resultSuggestions?.map((loc) =>
        update(loc, 'place_name', () => refactorPlaceName(loc?.place_name))
      );
      const findSuggestion =
        resultSuggestions.length > 0
          ? resultSuggestions.concat({
              place_name: 'find',
              id: 'find',
            })
          : [];

      !isEmpty(resultSuggestions)
        ? (setgqlSuggestions([{ items: findSuggestion, section: 'Search' }]),
          setErrorMessage(''))
        : (setgqlSuggestions([]),
          setErrorMessage(t('LOCATION_SEARCH.ERR_DEC_RELEASE')));
    },
  });

  const handleLocationInputChange = (inputValue) => {
    if (inputValue?.trim().length) {
      setIsLoading(true);
    } else {
      setgqlSuggestions([]);
      setErrorMessage('');
    }
  };

  const handleLocationChange = async (loc: string) => {
    await getLocation({
      variables: {
        address: encodeURIComponent(loc),
        countySearchEnabled: true,
      },
    });
  };

  const debounceApiFilteringSearch = useCallback(
    debounce(async (inputValue, selectedItem) => {
      if (inputValue && !selectedItem && inputValue.trim().length) {
        await handleLocationChange(inputValue);
      }
      setIsLoading(false);
    }, 300),
    []
  );

  const adobeLinkTrackEventFindLocation = () =>
    adobeLinkTrackEvent({
      name: 'find my location',
      location: `modal:${locationForAnalytics}`,
      type: 'internal',
    });

  const adobeLinkTrackEventSuggestedLocation = () =>
    adobeLinkTrackEvent({
      name: 'suggested location',
      location: `modal:${locationForAnalytics}`,
      type: 'internal',
    });

  return (
    <Layout.Stack
      className="full-width-layout-stack-search-input"
      css={{
        display: 'block',
      }}
    >
      <StyledLocationSearch
        customRender={(suggestion) =>
          suggestion?.section === 'Search' ? (
            <StyledLocationHeader>
              {t(`LOCATION_SEARCH.DID_YOU_MEAN`)}
            </StyledLocationHeader>
          ) : suggestion?.place_name === 'find' ? (
            <StyledLocationItems>
              <FindUserLocationContainer width="100%">
                <Layout.Group>
                  <IconSymbol
                    css={{ marginRight: '10px' }}
                    icon="my_location"
                  />
                  <Text
                    color="$core.color.brand.60"
                    fontSize="sm"
                    fontWeight="bold"
                  >
                    {t('Find my location')}
                  </Text>
                </Layout.Group>
              </FindUserLocationContainer>
            </StyledLocationItems>
          ) : (
            <StyledLocationItems grow space={0}>
              <Text
                color="$core.color.brand.60"
                fontSize="sm"
                fontWeight="bold"
              >
                {suggestion?.place_name}
              </Text>
            </StyledLocationItems>
          )
        }
        data-auto-testid={locationSearchInputDataTestId}
        data-testid={locationSearchInputDataTestId}
        hideLabel
        isClearable={true}
        isLoading={isLoading}
        isTypeAhead
        labelKey="place_name"
        onClear={() => {
          setgqlSuggestions([]);
          setErrorMessage('');
          setCustomLocation({});
        }}
        onInputChange={(inputValue) => {
          handleLocationInputChange(inputValue);
          debounceApiFilteringSearch(inputValue);
        }}
        onSearch={(inputValue, selectedOption) => {
          const newLocation = selectedOption || {};
          if (newLocation?.place_name === 'find') {
            adobeLinkTrackEventFindLocation();
            findUserLocation();
          } else if (newLocation?.place_name) {
            adobeLinkTrackEventSuggestedLocation();
            setCustomLocation(newLocation);
          } else if (inputValue) {
            setCustomLocation({ place_name: inputValue });
          }
        }}
        options={gqlSuggestions}
        placeholder={t('LOCATION_SEARCH.PLACEHOLDER')}
        rounded
        valueKey="place_name"
      />
      {errorMessage ? (
        <StyledLocationError alignItems="top" className="error-message">
          <IconSymbol
            className="error-icon"
            color="#C40000"
            icon="error"
            size="$sm"
          />
          <Text color="#C40000" fontSize="sm">
            {errorMessage}
          </Text>
        </StyledLocationError>
      ) : (
        ''
      )}
    </Layout.Stack>
  );
};
